import React, { useEffect, useState } from 'react';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { QUIZ_ORDER_ITEM } from '../itemIds';
import { QuizButton } from '~/ui/buttons/QuizButton';
import styled, { x } from '@xstyled/styled-components';
import { QuizOrderButtonUp } from './components/QuizOrderButtonUp';
import { QuizOrderButtonDown } from './components/QuizOrderButtonDown';
import { useQuizOrderStore } from './QuizOrderStore';
import SwitchAudio from '~/assets/mp3/switch1.mp3';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { TrainingState, useTrainingStore } from '~/training/state';
import { select } from '~/lib/select';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import { useEditorStore } from '~/shell/Store';

interface Props {
  variant?: string;
  id: string;
  text?: string;
  maxIndex: number;
  initialIndex: number;
  state: 'correct' | 'wrong';
}

const Wrapper = styled.divBox`
  margin-left: 10px;
  width: 100%;
  display: flex;
  align-items: center;
`;

const selector = (s: TrainingState) => select(s, 'saveQuestionAnswers');

export type QuizOrderItemDefinition = ItemDefinition<
  typeof QUIZ_ORDER_ITEM,
  Props
>;

export class QuizOrderItem extends Item<typeof QUIZ_ORDER_ITEM, Props> {
  protected render({
    children,
    id,
    text,
    maxIndex,
    state
  }: RenderProps<Props>) {
    const { array, moveDown, moveUp } = useQuizOrderStore();
    const { removeBlocker } = useSequenceStore();
    const { saveQuestionAnswers } = useTrainingStore(selector);
    const { editorMode } = useEditorStore();
    const [playAudioEffect] = useAudioEffect(SwitchAudio);
    const [currentState, setCurrentState] = useState(state);

    const currentIndex = array.findIndex(el => el.props.text === text);

    const content = text ? text : children;

    useEffect(() => {
      setCurrentState(array[currentIndex].props.state);
    }, [array]);

    useEffect(() => {
      if (currentState === 'correct') {
        saveQuestionAnswers(id);
        removeBlocker(id);
      }
    }, [currentState]);

    const isButtonUpDisabled =
      editorMode || currentIndex === 0 || currentState === 'correct';
    const isButtonDownDisabled =
      editorMode || currentIndex === maxIndex - 1 || currentState === 'correct';

    const handleUpClick = () => {
      try {
        playAudioEffect();
      } catch (error) {
        // Silently ignore audio effect errors
      }
      moveUp(currentIndex);
    };

    const handleDownClick = () => {
      try {
        playAudioEffect();
      } catch (error) {
        // Silently ignore audio effect errors
      }
      moveDown(currentIndex);
    };

    return (
      <Wrapper>
        <x.div fontSize={20} marginRight={20}>
          {currentIndex + 1}
        </x.div>
        <QuizButton
          w="100%"
          textAlign="left"
          pointerEvents="none"
          role="div"
          variant={currentState}
          tabIndex={-1}
        >
          {content}
        </QuizButton>
        <x.div
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          marginLeft={10}
          gap={5}
        >
          <QuizOrderButtonUp
            disabled={isButtonUpDisabled}
            onClickHandler={handleUpClick}
          />
          <QuizOrderButtonDown
            disabled={isButtonDownDisabled}
            onClickHandler={handleDownClick}
          />
        </x.div>
      </Wrapper>
    );
  }
}

registry[QUIZ_ORDER_ITEM] = QuizOrderItem;
